import React, { FC, useEffect, useState } from 'react';

import { GOOGLE_OPTIMIZE_ICONIZATION_PHASE_FIVE_TEST_ID } from 'constants/google-optimize';

import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Banner, { BannerProps } from '../Banner';
import type { JumbotronProps } from 'modules/common/components/Jumbotron';
import SimpleRequestFormEntry from './SimpleRequestFormEntry';
import RequestFormEntryForm from './RequestFormEntryForm';
import HomeRequestFormEntry from './HomeRequestFormEntry';
import LearningRequestFormEntry from './LearningRequestFormEntry';
import BusinessRequestFormEntry from './BusinessRequestFormEntry';
import { ReduxProps } from './BannerZipcode.container';

import type { VerticalPageJobType } from 'modules/job-types/types';
import { proCountSelector } from 'modules/marketing-pages/duck';
import styles from './BannerZipcode.module.scss';
import {
  getIsHomeRequestFormEntry,
  getIsLearningRequestFormEntry,
  getIsBusinessRequestFormEntry,
  getIsSimpleQuestionRequestFormEntry,
} from 'utils/job-type';
import { useGoogleOptimize } from 'utils/google-optimize-manager';
import { GOOGLE_OPTIMIZE_CHAT_FLOW_TEST_ID } from 'constants/google-optimize';
import AbTestFallbackLoadingDiv from './AbTestFallbackLoadingDiv';
import {sendFunnelEvent} from 'utils/telemetry';

const MINIMUM_TO_SHOW_PRO_COUNT = 30;

interface OwnProps {
  bannerProps?: Pick<BannerProps, 'shouldUseOverlayPadding' | 'shouldUseOverlayBackgroundColor'>;
  bgImg: string;
  bgImgObjectFit?: 'cover' | 'contain';
  city?: { key?: string; name: string };
  cityGroupName?: string;
  isJumbotronResponsive?: boolean;
  isRequestFormEntryEnabled?: boolean;
  jobType: VerticalPageJobType;
  jumbotronProps?: Omit<JumbotronProps, 'bgImg'>;
  prefecture?: { key?: string; name: string };
  shouldBannerBeFullHeight?: boolean;
  testVariant: string | null | { isInitialized: boolean; variant: string | null };
}

// TODO: move this;
declare module 'react' {
  interface SourceHTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    height?: number | string;
    width?: number | string;
  }
}

interface Window {
  dataLayer: Array<any>;
  google_optimize: {
    get: (id: string) => string;
  };
}

type Props = OwnProps & ReduxProps;

const BannerZipcode: FC<Props> = ({
  bgImg,
  city = { name: '' },
  cityGroupName = '',
  bannerProps = {},
  isRequestFormEntryEnabled = true,
  jobType,
  jumbotronProps = {},
  openRequestModal,
  prefecture = { name: '' },
  bgImgObjectFit,
  isJumbotronResponsive,
  shouldBannerBeFullHeight,
}) => {
  const { t } = useTranslation();
  const proCount = useSelector(proCountSelector);

  useEffect(() => {
    if (jobType.isConciergeBooking) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async () => {
        await openRequestModal({ city, jobTypeId: jobType.id, prefecture });
      })();
    }
  }, [city, jobType.isConciergeBooking, jobType.id, openRequestModal, prefecture]);

  const { landingPageSubtitle = '', requestType = '', seoName } = jobType.translations;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const jobTypeName = seoName || requestType;

  const titleAnterior = jobTypeName;
  const titlePosterior = `のプロが近くに${
    proCount >= MINIMUM_TO_SHOW_PRO_COUNT ? `${proCount.toLocaleString()}人` : ''
  }います`;

  const entryFormLabel = jobType.isDirectBooking
    ? landingPageSubtitle
    : t('marketingPages:requestFormEntryForm.label', {
        interpolation: { escapeValue: false },
        jobType: requestType,
      });

  const hasAtleastOneTitleString =
    prefecture.name || city.name || cityGroupName || titleAnterior || titlePosterior;
  const shouldShowHomeRequestFormEntry = getIsHomeRequestFormEntry(jobType);
  const shouldShowBusinessRequestFormEntry = getIsBusinessRequestFormEntry(jobType);
  const shouldShowSimpleRequestFormEntry = getIsSimpleQuestionRequestFormEntry(jobType);
  const [{ options }] = jobType.forms[0].fields;
  const ageFormOptions = ['10s', '20s', '30s', '40s', '50s', 'over60s'];
  const isFirstQuestionAge = getIsLearningRequestFormEntry(jobType);
  const isOnlyAgeField = options?.every((option) =>
    // option should be presented all the time but somehow it became "undefined" and crashed the application
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ageFormOptions.includes(option?.value?.toString())
  );

  const GOOGLE_OPTIMIZE_ICON_TEST_VARIATION = '1';
  const GOOGLE_OPTIMIZE_DURATION = 310;

  const AB_TEST_ICON_VT_ID_ARRAY = [
    'handyman',
    'sparekeyproduction',
    'personal-chef',
    'dog-therapist',
    'catering',
    'kimono-dressing-master',
    'manicurist',
    'makeup-artist',
    'furniture-upholstery-repair',
    'kimonocleaner',
    'photographer',
    'autorepair',
    'refrigeratorrepair',
    'kimonotailoring',
    'carremodeling',
    'pet-sitter',
    'tv-mounting',
    'hair-stylist',
    'furniturepainting',
    'mover',
    'automotiveinteriorspecialist',
    'ikea-nitori-furniture-assembly',
    'pre-owned-condominium-renovator',
    'pet-trimmer',
    'pettrainer',
    'lifecoaching',
    'washing-machine-cleaner',
    'event-florist',
    'beautyadvisor',
    'food-truck',
    'computer-setup-support',
    'motorcyclerepair',
    'civil-engineer',
    'photographer-baby-photos',
    'photographer-headshots',
    'pianist',
    'photographer-maternity',
    'furniture-carpenter',
    'chef',
    'singer',
    'event-planner',
    'dj',
    'dance-performer',
    'mc',
    'fashion-advisor',
    'college-entrance-exam-tutor',
    'media-translator',
    'wedding-planner',
    'private-investigator',
    'pastry-chef',
    'stylist',
    'refrigerator-cleaner',
    'bartender',
    'nutritionist',
    'food-coordinator',
    'highschool-entrance-exam-tutor',
    'middle-school-entrance-exam-tutor',
    'computer-virus-support',
    'buddhist-priest-monk',
    'photographer-engagement',
    'photographer-corporate',
    'estate-items-liquidator',
    'second-hand-purchasing',
    'antique-appraisal',
    'pet-aroma',
    'photographer-wedding',
    'cardboard-production',
    'fooddeliveryservice',
    'photographer-family-portrait',
    'marriage-arranger',
    'photographer-753-ceremony',
    'travelagent',
    'photographer-party-event',
    'photographer-coming-of-age',
  ];

  // remove when AB test is done for GOOGLE_OPTIMIZE_ICONIZATION_PHASE_FIVE_TEST_ID
  const [isLoading, setIsLoading] = useState(
    Boolean(AB_TEST_ICON_VT_ID_ARRAY.includes(jobType.id))
  );

  const isTestVariation =
    useGoogleOptimize(GOOGLE_OPTIMIZE_ICONIZATION_PHASE_FIVE_TEST_ID, GOOGLE_OPTIMIZE_DURATION) ===
      GOOGLE_OPTIMIZE_ICON_TEST_VARIATION && AB_TEST_ICON_VT_ID_ARRAY.includes(jobType.id);

  // to avoid a flash of the RequestFormEntryForm while Google Optimize sets up
  // This only shows on the verticals for the AB test
  useEffect(() => {
    setIsLoading(true);
    const timeoutId = setTimeout(() => setIsLoading(false), GOOGLE_OPTIMIZE_DURATION);
    return () => clearTimeout(timeoutId);
  }, []);

  let componentToDisplay;
  if (shouldShowHomeRequestFormEntry) {
    componentToDisplay = (
      <HomeRequestFormEntry
        firstField={jobType.forms[0].fields[0]}
        label={entryFormLabel}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (propertyType) => {
          // TODO: Don't repeat this whole block for each entry form component onSubmit
          let useChatFlow = false;
          try {
            (window as unknown as Window).dataLayer.push({ event: 'launchRequestForm' });
            useChatFlow =
              (window as unknown as Window).google_optimize.get(
                GOOGLE_OPTIMIZE_CHAT_FLOW_TEST_ID
              ) === '1';
          } catch (_err) {
            // intentionally left blank
          }
          if (useChatFlow) {
            sendFunnelEvent(jobType.id, 'startRequestFromVerticalLP', '1');
            document.location.href = `/start/v2/${jobType.id}${
              propertyType === 'other' ? '' : `?propertyType=${propertyType}`
            }`;
          } else {
            sendFunnelEvent(jobType.id, 'startRequestFromVerticalLP', '0');
            await openRequestModal({
              city,
              cityGroupName,
              jobTypeId: jobType.id,
              prefecture,
              propertyType,
            });
          }
        }}
      />
    );
  } else if (isFirstQuestionAge && isOnlyAgeField) {
    componentToDisplay = (
      <LearningRequestFormEntry
        options={options}
        title={jobType.forms[0].title}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (age) => {
          let useChatFlow = false;
          try {
            (window as unknown as Window).dataLayer.push({ event: 'launchRequestForm' });
            useChatFlow =
              (window as unknown as Window).google_optimize.get(
                GOOGLE_OPTIMIZE_CHAT_FLOW_TEST_ID
              ) === '1';
          } catch (_err) {
            // intentionally left blank
          }
          if (useChatFlow) {
            sendFunnelEvent(jobType.id, 'startRequestFromVerticalLP', '1');
            document.location.href = `/start/v2/${jobType.id}?age=${age}`;
          } else {
            sendFunnelEvent(jobType.id, 'startRequestFromVerticalLP', '0');
            await openRequestModal({
              city,
              cityGroupName,
              jobTypeId: jobType.id,
              prefecture,
              age,
            });
          }
        }}
      />
    );
  } else if (shouldShowBusinessRequestFormEntry) {
    componentToDisplay = (
      <BusinessRequestFormEntry
        firstField={jobType.forms[0].fields[0]}
        title={jobType.forms[0].title}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (corporateindividual) => {
          await openRequestModal({
            city,
            cityGroupName,
            corporateindividual,
            jobTypeId: jobType.id,
            prefecture,
          });
        }}
      />
    );
  } else if (shouldShowSimpleRequestFormEntry || isTestVariation) {
    componentToDisplay = (
      <SimpleRequestFormEntry
        jobTypeName={jobType.translations.requestType}
        // eslint-disable-next-line react/jsx-no-bind
        isTestVariant={isTestVariation}
        onSubmit={async () => {
          await openRequestModal({
            city,
            cityGroupName,
            jobTypeId: jobType.id,
            prefecture,
          });
        }}
      />
    );
  } else if (isLoading) {
    componentToDisplay = <AbTestFallbackLoadingDiv />;
  } else
    componentToDisplay = (
      <RequestFormEntryForm
        isZipcodeInputShown={!jobType.isConciergeBooking}
        label={entryFormLabel}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (address) => {
          await openRequestModal({
            address,
            city,
            cityGroupName,
            jobTypeId: jobType.id,
            prefecture,
          });
        }}
      />
    );
  const EntryForm = componentToDisplay;

  return (
    <>
      <Banner
        bgImg={bgImg}
        alt={jobType.title}
        bgImgObjectFit={bgImgObjectFit}
        fullHeight={shouldBannerBeFullHeight}
        isJumbotronResponsive={isJumbotronResponsive}
        jumbotronProps={jumbotronProps}
        jobTypeId={jobType.id}
        {...bannerProps}
      >
        {hasAtleastOneTitleString && (
          <h1 className={classNames(styles['header'])}>
            <span className={classNames(styles['emphasis'])}>
              {prefecture.name}
              {cityGroupName}
              {city.name}
            </span>
            <span className={classNames(styles['emphasis'])}>{titleAnterior}</span>
            {jobType.isDirectBooking || jobType.isConciergeBooking ? null : titlePosterior}
          </h1>
        )}
        {isRequestFormEntryEnabled && EntryForm}
      </Banner>
    </>
  );
};

export type { OwnProps as BannerZipcodeProps };
export default BannerZipcode;
